import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Premium first-paint loader.
 * Shows brand mark + animated progress bar for ~1.6s then unmounts.
 * Respects prefers-reduced-motion (renders nothing).
 */
export default function Loader() {
  const [show, setShow] = useState(true);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setShow(false);
      return;
    }
    const t = setTimeout(() => setShow(false), 1500);
    return () => clearTimeout(t);
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="page-loader"
          initial={{ opacity: 1 }}
          exit={{
            opacity: 0,
            y: "-100%",
            transition: { duration: 0.7, ease: [0.76, 0, 0.24, 1] },
          }}
          className="fixed inset-0 z-[999] bg-[#050505] flex items-center justify-center overflow-hidden"
        >
          {/* Grid backdrop */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "linear-gradient(to right, #E2FF31 1px, transparent 1px), linear-gradient(to bottom, #E2FF31 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />

          <div className="relative w-full max-w-md px-8">
            {/* Brand + status */}
            <div className="flex items-center justify-between mb-6">
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="flex items-center gap-2"
              >
                <span className="inline-block w-2.5 h-2.5 bg-[#E2FF31] rounded-sm rotate-45" />
                <span className="font-display text-lg font-semibold tracking-tight">
                  Elevate<span className="text-[#E2FF31]">.</span>studios
                </span>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.25, duration: 0.4 }}
                className="font-mono-tag text-[10px] text-white/50"
              >
                loading…
              </motion.div>
            </div>

            {/* Progress bar */}
            <div className="relative h-[2px] w-full bg-white/10 overflow-hidden rounded-full">
              <motion.div
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 1.3, ease: [0.65, 0, 0.35, 1] }}
                className="h-full bg-[#E2FF31]"
              />
            </div>

            {/* Line list */}
            <div className="mt-6 space-y-1.5 font-mono-tag text-[10px] text-white/40">
              {[
                "Booting studio interface",
                "Preparing case studies",
                "Warming pixels",
              ].map((line, i) => (
                <motion.div
                  key={line}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.35 + i * 0.28, duration: 0.4 }}
                  className="flex items-center gap-3"
                >
                  <span className="text-[#E2FF31]">›</span> {line}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
